import React, {useEffect, useRef} from 'react'
import {useVirtualizer} from '@tanstack/react-virtual'
import styles from './UIGameFeedVirtualScrollContainer.module.scss'

interface UIGameFeedVirtualScrollContainerProps {
    children: React.ReactElement[]
    scrollToChildIndex?: number | undefined
    autoScrollToBottom: boolean
}

export function UIGameFeedVirtualScrollContainer({
    children,
    scrollToChildIndex,
    autoScrollToBottom,
}: UIGameFeedVirtualScrollContainerProps) {
    const isMouseOverRef = useRef<boolean>(false)
    const parentRef = useRef<HTMLDivElement>(null)
    const previousScrollToIndexRef = useRef<number>(-1)
    const lastScrollTimeRef = useRef<number>(0)

    const virtualizer = useVirtualizer({
        count: children.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 50,
    })

    useEffect(() => {
        if(scrollToChildIndex != null && scrollToChildIndex >= 0) {
            const currentTime = Date.now()
            const timeSinceLastScroll = currentTime - lastScrollTimeRef.current
            const previousScrollToIndex = previousScrollToIndexRef.current
            const jumpDistance = Math.abs(scrollToChildIndex - previousScrollToIndex)
            const scrollDuration = 500
            const distanceToSmoothScroll = 5
            const shouldUseInstantScroll = timeSinceLastScroll < scrollDuration || jumpDistance > distanceToSmoothScroll

            if(shouldUseInstantScroll) {
                virtualizer.scrollToIndex(scrollToChildIndex, {
                    align: 'center',
                    behavior: 'auto',
                })
            } else {
                virtualizer.scrollToIndex(scrollToChildIndex, {
                    align: 'center',
                    behavior: 'smooth',
                })
            }

            lastScrollTimeRef.current = currentTime
            previousScrollToIndexRef.current = scrollToChildIndex
        }
    }, [scrollToChildIndex])

    useEffect(() => {
        if(isMouseOverRef.current) return
        if(autoScrollToBottom && scrollToChildIndex === undefined && children.length > 0) {
            virtualizer.scrollToIndex(children.length - 1, {
                align: 'end',
                behavior: 'auto',
            })
        }
    }, [children, scrollToChildIndex, virtualizer, autoScrollToBottom])

    const items = virtualizer.getVirtualItems()

    return (
        <div
            onMouseEnter={() => isMouseOverRef.current = true}
            onMouseLeave={() => isMouseOverRef.current = false}
            ref={parentRef}
            className={styles.virtualContainer}
        >
            <div
                className={styles.virtualScroller}
                style={{
                    height: `${virtualizer.getTotalSize()}px`,
                }}
            >
                {items.map((virtualItem) => {
                    const child = children[virtualItem.index]
                    if(child == null) return null

                    return (
                        <div
                            key={virtualItem.key}
                            data-index={virtualItem.index}
                            ref={virtualizer.measureElement}
                            className={styles.scrollItemContainer}
                            style={{
                                transform: `translateY(${virtualItem.start}px)`,
                            }}
                        >
                            {child}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
